/**
 * MP Product Pack Video (product card)
 * ADDED: 2026-08-05 by MeshPlanet
 *
 * <mp-pack-video> pairs a "see what it can pack" link with its own native
 * <dialog>. The <video> lives in the dialog from the start, so opening it never
 * has to swap a source — it just plays the element that is already there.
 *
 * The play() call has to stay in the same task as the click that opened the
 * dialog: browsers only allow sound while the call still carries the user
 * gesture. Anything deferred (rAF, transitionend, await) loses it and the video
 * comes up silent, so showModal() and play() run back to back below. If the
 * browser rejects it anyway, the video is muted and retried so it still plays
 * instead of sitting on a frozen poster.
 *
 * Data attributes:
 *   data-mp-pack-open     the link that opens the modal
 *   data-mp-pack-dialog   the modal itself
 *   data-mp-pack-close    close button inside the modal
 *   data-start-muted      'true' to open muted (sound off until the user asks)
 */
(function () {
  'use strict';

  class MpPackVideo extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.dialog = this.querySelector('[data-mp-pack-dialog]');
      const trigger = this.querySelector('[data-mp-pack-open]');
      if (!this.dialog || !trigger) return;

      // Shopify's video_tag does not guarantee the attribute, and without it iOS
      // Safari takes the video fullscreen instead of playing it in the modal.
      this.querySelectorAll('video').forEach((video) => {
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
      });

      trigger.addEventListener('click', this.open);

      // Fires for the close button, Escape and the backdrop alike, so the video
      // is reset no matter how the modal was dismissed.
      this.dialog.addEventListener('close', () => {
        this.stop();
        this.releaseScroll();
      });

      // The dialog is nested inside <product-card>, whose own click handler reads
      // any click that is not on a button/input as "go to the PDP". Video
      // controls do not pass that check, so scrubbing the timeline would navigate
      // away mid-play — clicks are kept inside the dialog instead. That also
      // means the close button is handled here rather than higher up.
      this.dialog.addEventListener('click', (event) => {
        event.stopPropagation();

        // A click that lands on the dialog itself is a click on the backdrop —
        // the inner panel covers the whole box.
        if (event.target === this.dialog || event.target.closest('[data-mp-pack-close]')) {
          this.dialog.close();
        }
      });
    }

    // The listeners live on this element's own children, so they go away with the
    // subtree. Only the page-level scroll lock has to be undone — a section
    // re-render can drop the element while the modal is open.
    disconnectedCallback() {
      this.releaseScroll();
    }

    open = () => {
      const { dialog } = this;

      if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;

      document.documentElement.setAttribute('scroll-lock', '');
      dialog.showModal();

      const video = dialog.querySelector('video');
      if (!video) return;

      video.currentTime = 0;
      video.muted = this.getAttribute('data-start-muted') === 'true';

      video.play()?.catch(() => {
        // Autoplay policy refused sound. Muted playback is always allowed, so
        // fall back to it rather than leaving the modal on its poster.
        video.muted = true;
        video.play()?.catch(() => {});
      });
    };

    stop() {
      const video = this.dialog?.querySelector('video');
      if (!video) return;

      video.pause();
      video.currentTime = 0;
    }

    releaseScroll() {
      // Another card (or another component) may still be open — only the last one
      // out unlocks the page.
      if (document.querySelector('dialog[open]')) return;
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  if (!customElements.get('mp-pack-video')) {
    customElements.define('mp-pack-video', MpPackVideo);
  }
})();
